import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { AccessibleIcon } from "@radix-ui/react-accessible-icon";
import { BrandLogo } from "@/components/atoms";
import { useTenantBrand } from "@/hooks";
import "./Navbar.scss";

/**
 * Props for the Navbar component
 * @interface NavbarProps
 */
interface NavbarProps {
  /** Title to display in the navigation bar */
  title: string;
  /** Route for the back chevron (default: home) */
  backTo?: string;
}

/**
 * Props for the Navbar container
 * @interface NavbarContainerProps
 * @extends NavbarProps
 */
interface NavbarContainerProps extends NavbarProps {
  /** Content to render below the navigation bar */
  children: React.ReactNode;
}

/**
 * Navigation bar component
 * @component
 * @param {NavbarProps} props - Component props
 * @returns Navbar component
 */
const Navbar: React.FC<NavbarProps> = ({ title, backTo = "/" }) => {
  const { getLogoSrc, logoAlt } = useTenantBrand();

  return (
    <nav className="navbar">
      <div className="navbar__section navbar__section--left">
        <Link to={backTo} className="navbar__link navbar__link--back">
          <AccessibleIcon label="Volver">
            <ChevronLeft className="navbar__arrow" direction="left" />
          </AccessibleIcon>
        </Link>
      </div>

      <div className="navbar__section navbar__section--center">
        <h1 className="navbar__title">{title}</h1>
      </div>

      <div className="navbar__section navbar__section--right">
        <Link
          to="/"
          className="navbar__link navbar__link--home"
          aria-label="Ir al inicio"
        >
          <BrandLogo
            src={getLogoSrc("navbar")}
            alt={logoAlt}
            className="navbar__logo"
            loading="eager"
          />
        </Link>
      </div>
    </nav>
  );
};

/**
 * Container that includes the navigation bar and main content
 * @component
 * @param {NavbarContainerProps} props - Component props
 * @returns NavbarContainer component
 */
export const NavbarContainer: React.FC<NavbarContainerProps> = ({
  title,
  backTo,
  children,
}) => {
  return (
    <div className="navbar-layout container">
      <Navbar title={title} backTo={backTo} />
      <main className="navbar-layout__content">{children}</main>
    </div>
  );
};

export default Navbar;
